import dayjs from 'dayjs';
import type { PipelineStatusKey } from '@/features/dashboard/components/dashboardStatusDataHelpers';
import type { BuildDto } from '@flowqa/shared';

type LineChartDatum = { [key: string]: number | string; label: string };

type PipelineMiniMetricChartData = {
  passRateChartData: LineChartDatum[];
  frequencyChartData: LineChartDatum[];
  durationChartData: LineChartDatum[];
  passRate: number | null;
  frequencyPerDay: number | null;
  avgDurationSeconds: number | null;
};

type DayBucket = {
  total: number;
  completed: number;
  failed: number;
  durationSumSeconds: number;
  durationCount: number;
};

const resolveBuildTimestamp = (build: BuildDto): Date | null =>
  build.finishedAt ?? build.startedAt ?? build.createdAt;

const resolveBuildDurationSeconds = (build: BuildDto): number | null => {
  if (build.startedAt === null || build.finishedAt === null) return null;
  const durationMs = build.finishedAt.getTime() - build.startedAt.getTime();
  return Number.isFinite(durationMs) && durationMs >= 0 ? durationMs / 1000 : null;
};

const createDayBucket = (): DayBucket => ({
  total: 0,
  completed: 0,
  failed: 0,
  durationSumSeconds: 0,
  durationCount: 0,
});

export const buildIsoChartDates = (startIso: string, endIso: string): string[] => {
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime()) || start > end) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(start);
  cursor.setUTCHours(0, 0, 0, 0);
  const endDay = new Date(end);
  endDay.setUTCHours(0, 0, 0, 0);

  while (cursor <= endDay) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
};

export const resolveBuildChartTimestamp = (build: BuildDto): Date | null =>
  build.finishedAt ?? build.startedAt ?? build.createdAt;

export const isBuildWithinSelectedWindow = (
  build: BuildDto,
  startIso: string | null,
  endIso: string | null,
): boolean => {
  const timestamp = resolveBuildChartTimestamp(build);
  if (timestamp === null || Number.isNaN(timestamp.getTime())) {
    return false;
  }
  if (startIso !== null) {
    const start = new Date(startIso);
    if (!Number.isNaN(start.getTime()) && timestamp < start) {
      return false;
    }
  }
  if (endIso !== null) {
    const end = new Date(endIso);
    if (!Number.isNaN(end.getTime()) && timestamp > end) {
      return false;
    }
  }
  return true;
};

export const buildPreviousCustomWindow = (
  startDay: string | null,
  endDay: string | null,
): { startIso: string | null; endIso: string | null } => {
  const rawStart = dayjs(startDay ?? endDay ?? null);
  const rawEnd = dayjs(endDay ?? startDay ?? null);
  if (!rawStart.isValid() || !rawEnd.isValid()) {
    return { startIso: null, endIso: null };
  }
  const start = rawStart.isAfter(rawEnd) ? rawEnd.startOf('day') : rawStart.startOf('day');
  const end = rawStart.isAfter(rawEnd) ? rawStart.endOf('day') : rawEnd.endOf('day');
  const spanDays = end.startOf('day').diff(start.startOf('day'), 'day') + 1;
  const previousEnd = start.subtract(1, 'day').endOf('day');
  const previousStart = previousEnd.subtract(spanDays - 1, 'day').startOf('day');
  return {
    startIso: previousStart.toISOString(),
    endIso: previousEnd.toISOString(),
  };
};

export const buildCountsFromBuilds = (
  builds: BuildDto[] | null,
): Partial<Record<PipelineStatusKey, number>> => {
  if (!builds) {
    return { queued: 0, running: 0, completed: 0, failed: 0, none: 0 };
  }
  return builds.reduce<Partial<Record<PipelineStatusKey, number>>>(
    (acc, build) => {
      acc[build.status] = (acc[build.status] ?? 0) + 1;
      acc.none = 0;
      return acc;
    },
    { queued: 0, running: 0, completed: 0, failed: 0, none: 0 },
  );
};

export const calculateBuildPassRate = (
  counts: Partial<Record<PipelineStatusKey, number>>,
): number | null => {
  const completed = counts.completed ?? 0;
  const failed = counts.failed ?? 0;
  const denominator = completed + failed;
  if (denominator <= 0) {
    return null;
  }
  return (completed / denominator) * 100;
};

export const buildPipelineMiniMetricChartDataFromBuilds = (
  builds: BuildDto[],
  chartDates: string[],
): PipelineMiniMetricChartData => {
  const buckets = new Map<string, DayBucket>();
  let completed = 0;
  let failed = 0;
  let durationSumSeconds = 0;
  let durationCount = 0;

  builds.forEach((build) => {
    const timestamp = resolveBuildTimestamp(build);
    if (timestamp === null || Number.isNaN(timestamp.getTime())) {
      return;
    }

    const dayKey = timestamp.toISOString().slice(0, 10);
    const bucket = buckets.get(dayKey) ?? createDayBucket();
    bucket.total += 1;
    if (build.status === 'completed') {
      bucket.completed += 1;
      completed += 1;
    }
    if (build.status === 'failed') {
      bucket.failed += 1;
      failed += 1;
    }

    const durationSeconds = resolveBuildDurationSeconds(build);
    if (durationSeconds !== null) {
      bucket.durationSumSeconds += durationSeconds;
      bucket.durationCount += 1;
      durationSumSeconds += durationSeconds;
      durationCount += 1;
    }

    buckets.set(dayKey, bucket);
  });

  const dates = chartDates.length > 0 ? chartDates : Array.from(buckets.keys()).sort();
  const passRateChartData = dates.map((date) => {
    const bucket = buckets.get(date) ?? createDayBucket();
    const denominator = bucket.completed + bucket.failed;
    return {
      label: date,
      passRate: denominator > 0 ? (bucket.completed / denominator) * 100 : 0,
    };
  });
  const frequencyChartData = dates.map((date) => ({
    label: date,
    frequency: (buckets.get(date) ?? createDayBucket()).total,
  }));
  const durationChartData = dates.map((date) => {
    const bucket = buckets.get(date) ?? createDayBucket();
    return {
      label: date,
      duration: bucket.durationCount > 0 ? bucket.durationSumSeconds / bucket.durationCount : 0,
    };
  });

  const passRateDenominator = completed + failed;

  return {
    passRateChartData,
    frequencyChartData,
    durationChartData,
    passRate: passRateDenominator > 0 ? (completed / passRateDenominator) * 100 : null,
    frequencyPerDay: builds.length > 0 && dates.length > 0 ? builds.length / dates.length : null,
    avgDurationSeconds: durationCount > 0 ? durationSumSeconds / durationCount : null,
  };
};
